//src/pages/JumboMailLogin.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { loginUser } from "../redux/slices/authSlice";
import { motion, AnimatePresence } from "framer-motion";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  AlertCircle,
  Fingerprint,
  KeyRound,
  LogIn,
  Github,
  Mail,
  Building2,
  Languages,
  CheckCircle2,
} from "lucide-react";

/**
 * NOTE: put your logo file in public/ as `jumbo_mail_logo.jpeg`
 * or pass a correct path via `backgroundImage` prop.
 */
export default function LoginPage({ backgroundImage = "/jumbo_mail_logo.jpeg" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error, token, merchantId } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    try {
      localStorage.setItem('source', 'jumbomail');
    } catch (e) { }
  }, []);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [remember, setRemember] = useState(true);
  const [formError, setFormError] = useState("");
  const [strength, setStrength] = useState("Weak");
  const [locale, setLocale] = useState("en");

  const usernameRef = useRef(null);

  const canSubmit = useMemo(
    () => username.trim().length >= 3 && password.trim().length >= 6 && !loading,
    [username, password, loading]
  );

  // Post-login bootstrap
  useEffect(() => {
    if (isAuthenticated && token && merchantId) {
      (async () => {
        try {
          const carrierUrl =
            `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_ALLOCATED_CARRIER_DETAILS}`;
          const cRes = await axios.post(
            carrierUrl,
            { merchant_id: merchantId },
            { headers: { Authorization: token } }
          );
          localStorage.setItem(
            "allocatedCarriers",
            JSON.stringify(cRes.data?.allocated_carrier_details || [])
          );

          const parentClientId = localStorage.getItem("parentClientId");
          const profileUrl =
            `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_GET_PROFILE}${parentClientId}`;
          const pRes = await axios.get(profileUrl, {
            headers: { Authorization: token },
          });
          localStorage.setItem("profileData", JSON.stringify(pRes.data));
        } catch (e) {
          console.error("Post-login fetch error:", e);
        } finally {
          navigate("/all-shipments", { replace: true });
        }
      })();
    }
  }, [isAuthenticated, token, merchantId, navigate]);

  // focus username
  useEffect(() => {
    requestAnimationFrame(() => usernameRef.current?.focus());
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    if (!canSubmit) return;
    dispatch(loginUser({ username: username.trim(), password, source: 'jumbomail' }));
  };

  const togglePassword = () => setShowPassword((v) => !v);

  const onKeyEvent = (e) => {
    setCapsLock(e.getModifierState && e.getModifierState("CapsLock"));
  };

  useEffect(() => {
    if (password.length < 6) setStrength("Weak");
    else if (/[A-Z]/.test(password) && /[0-9]/.test(password)) setStrength("Strong");
    else setStrength("Medium");
  }, [password]);

  const slides = [
    { name: "Fast Delivery", image: backgroundImage },
    { name: "Secure Mail", image: "/expert.png" },
    { name: "Global Reach", image: "/lady_notification.png" },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#F8EFE6] flex items-center justify-center p-6">
      {/* Subtle cream texture - soft vignette */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/0 to-black/3 opacity-20" />
      </div>

      <div className="grid w-full max-w-6xl grid-cols-1 gap-6 md:grid-cols-2 items-center">
        {/* Brand Side */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden md:flex flex-col justify-center items-center gap-6 rounded-3xl bg-white/60 p-8 shadow-md ring-1 ring-black/5"
          style={{ backdropFilter: "blur(6px)" }}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-2xl bg-[#E8F6FB] p-6 ring-1 ring-[#2A8FD3]/20">
              <img
                src={backgroundImage}
                alt="Jumbo Mail"
                className="h-36 w-auto select-none"
                style={{ imageRendering: "auto" }}
              />
            </div>

            <h1 className="text-3xl font-extrabold text-[#073742]">Jumbo Mail</h1>
            <p className="text-center max-w-xs text-sm text-[#0b5566]/80">
              A friendly, enterprise-grade mail & shipments platform — secure, reliable and delightful.
            </p>
          </div>

          <Carousel
            autoPlay
            infiniteLoop
            showThumbs={false}
            showStatus={false}
            showArrows={false}
            interval={3000}
            className="w-full mt-4 rounded-xl overflow-hidden"
          >
            {slides.map((s, i) => (
              <div key={i} className="flex flex-col items-center justify-center h-56 bg-transparent">
                <img src={s.image} alt={s.name} className="h-28 object-contain" />
                <p className="mt-4 text-lg font-semibold text-[#07374a]">{s.name}</p>
              </div>
            ))}
          </Carousel>

          <div className="mt-4 flex items-center gap-4 text-[#07374a]/85">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4" /> ISO-ready</div>
            <div className="flex items-center gap-2"><Fingerprint className="h-4" /> 2FA</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4" /> SLA</div>
          </div>
        </motion.div>

        {/* Form Side */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-[#ffffff] p-8 shadow-lg ring-1 ring-black/5"
        >
          <h2 className="flex items-center gap-2 text-2xl font-bold text-[#07374a]">
            <LogIn /> Sign in
          </h2>
          <p className="mt-1 text-sm text-[#0b5566]/80">Use your Jumbo Mail credentials</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            {/* Username */}
            <div className="relative">
              <input
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="peer w-full rounded-xl border border-[#e6eef4] bg-[#f7fbfc] px-12 py-3 text-[#07374a] placeholder-transparent outline-none transition focus:border-[#2A8FD3] focus:ring-2 focus:ring-[#2A8FD3]/20"
                autoComplete="username"
              />
              <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#0b5566]/70" />
              <label
                htmlFor="username"
                className="absolute left-12 -top-2.5 text-sm font-medium text-[#0b5566] peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-[#6b8490] transition-all"
              >
                Username
              </label>
            </div>

            {/* Password */}
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={onKeyEvent}
                className="peer w-full rounded-xl border border-[#e6eef4] bg-[#f7fbfc] px-12 py-3 text-[#07374a] placeholder-transparent outline-none transition focus:border-[#2A8FD3] focus:ring-2 focus:ring-[#2A8FD3]/20"
                autoComplete="current-password"
              />
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#0b5566]/70" />
              <button
                type="button"
                onClick={togglePassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-2 bg-[#eef9ff] ring-1 ring-[#2A8FD3]/10 hover:bg-[#e2f4ff] focus:outline-none focus:ring-2 focus:ring-[#2A8FD3]/30"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-[#07374a]" />
                ) : (
                  <Eye className="h-4 w-4 text-[#07374a]" />
                )}
              </button>
              <label
                htmlFor="password"
                className="absolute left-12 -top-2.5 text-sm font-medium text-[#0b5566] peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-[#6b8490] transition-all"
              >
                Password
              </label>
            </div>

            {/* Strength meter */}
            {password && (
              <div className="space-y-2">
                <div className="h-1.5 w-full rounded-full bg-[#e6eef4] overflow-hidden">
                  <div
                    className={`h-full ${
                      strength === "Weak"
                        ? "w-1/3 bg-[#ff7b7b]"
                        : strength === "Medium"
                        ? "w-2/3 bg-[#ffb66b]"
                        : "w-full bg-[#2A8FD3]"
                    } transition-all`}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span
                    className={
                      strength === "Weak"
                        ? "text-[#ff7b7b]"
                        : strength === "Medium"
                        ? "text-[#ffb66b]"
                        : "text-[#2A8FD3]"
                    }
                  >
                    Strength: {strength}
                  </span>
                  {capsLock && (
                    <span className="text-[#c08a00] flex items-center gap-1">
                      <KeyRound className="h-4" /> Caps Lock on
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Remember */}
            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 text-[#07374a]">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-[#cfe8f6] bg-[#f7fbfc] text-[#2A8FD3] focus:ring-[#2A8FD3]/40"
                />{" "}
                Remember me
              </label>
              <Link to="/forgot-password" className="text-sm text-[#2A8FD3] hover:underline">
                Forgot password?
              </Link>
            </div>

            {/* Error */}
            <AnimatePresence>
              {(error || formError) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm text-[#b44b4b] flex items-center gap-1"
                >
                  <AlertCircle /> {error || formError}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit */}
            <motion.button
              whileTap={{ scale: canSubmit ? 0.98 : 1 }}
              disabled={!canSubmit}
              type="submit"
              className={`group relative w-full flex justify-center items-center gap-2 rounded-xl py-3 font-semibold transition focus:outline-none focus:ring-2 focus:ring-[#2A8FD3]/30
                ${
                  canSubmit
                    ? "bg-gradient-to-r from-[#2A8FD3] to-[#57C7E8] text-white shadow-md"
                    : "bg-[#f0f6f9] text-[#9aa9b0] cursor-not-allowed"
                }`}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
              {loading ? "Signing in..." : "Sign in"}
            </motion.button>

            {/* SSO */}
            <div className="relative mt-6">
              <div className="flex items-center gap-2">
                <span className="flex-1 h-px bg-[#e6eef4]" />
                <span className="text-xs text-[#6b8490]">or continue with</span>
                <span className="flex-1 h-px bg-[#e6eef4]" />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 rounded-lg border border-[#e6eef4] bg-[#f7fbfc] py-2 text-[#07374a] hover:bg-[#eef9ff]"
                >
                  <Mail className="h-4" />
                  Google
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 rounded-lg border border-[#e6eef4] bg-[#f7fbfc] py-2 text-[#07374a] hover:bg-[#eef9ff]"
                >
                  <Building2 className="h-4" />
                  Microsoft
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 rounded-lg border border-[#e6eef4] bg-[#f7fbfc] py-2 text-[#07374a] hover:bg-[#eef9ff]"
                >
                  <Github className="h-4" />
                  GitHub
                </button>
              </div>
            </div>

            {/* Footer links */}
            <p className="text-center text-[#07374a] text-sm mt-4">
              Don’t have an account?{" "}
              <Link to="/signUp" className="underline decoration-[#2A8FD3]/40 underline-offset-2 hover:text-[#2A8FD3]">
                Sign up
              </Link>
            </p>

            <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-[#6b8490]">
              <Link to="/terms-and-conditions" className="hover:underline">
                Terms
              </Link>
              <Link to="/privacy-policy" className="hover:underline">
                Privacy
              </Link>
              <Link to="/refund-policy" className="hover:underline">
                Refunds
              </Link>
              <button type="button" className="flex items-center gap-1 hover:underline">
                <Languages className="h-4" /> {locale.toUpperCase()}
              </button>
            </div>

            <div className="mt-4 text-xs text-center text-[#6b8490] flex items-center justify-center gap-1">
              <ShieldCheck className="h-4" /> Protected by enterprise-grade encryption
            </div>
          </form>
        </motion.div>
      </div>

      {/* Small style overrides for carousel dots & autofill */
      /* Keep autofill behavior consistent with new light inputs */ }
      <style>{`
        .carousel .control-dots { bottom: 6px; z-index: 10; }
        .carousel .control-dots .dot { background: #2A8FD3; width: 28px; height: 4px; opacity: 0.35; border-radius: 999px; transition: opacity .2s, transform .2s; margin: 0 6px; }
        .carousel .control-dots .dot.selected, .carousel .control-dots .dot:hover { opacity: 1; transform: scaleX(1.05); background: #07374a; }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px #f7fbfc inset !important;
          -webkit-text-fill-color: #07374a !important;
          caret-color: #07374a !important;
          transition: background-color 99999s ease-in-out 0s;
        }
        input:-webkit-autofill ~ label {
          top: -10px !important;
          font-size: 0.875rem !important;
          color: #6b8490 !important;
        }

        input:-moz-autofill {
          box-shadow: 0 0 0px 1000px #f7fbfc inset !important;
          -moz-text-fill-color: #07374a !important;
          caret-color: #07374a !important;
        }
        input:-moz-autofill ~ label {
          top: -10px !important;
          font-size: 0.875rem !important;
          color: #6b8490 !important;
        }
      `}</style>
    </div>
  );
}
